import { createApi } from '@reduxjs/toolkit/query/react'
import { DateTime } from 'luxon'

import { ONBOARDING_STORAGE_KEY_PREFIX } from '@Onboarding/hooks/useStorageForOnboarding'
import type {
  HarvesterChannelList,
  HarvesterProviderList,
} from '@Store/types/harvester'

import customFetchBase from '../base-query'
import type { PaginatedQueryParams, PaginatedResponse } from '../types'
import type {
  InvoiceDownloadResponse,
  SimpleInvoiceResponse,
  UpdateCreditCardQueryArgs,
  UpgradeSubscriptionQueryArgs,
} from '../types/billing'
import type { Brand } from '../types/brand'
import type { Category } from '../types/category'
import type {
  BreakdownInfo,
  Company,
  CompanyForSelect,
  ProductLimits,
} from '../types/company'
import type { Competitor } from '../types/competitor'
import type { DashboardTopbarResponse, IndexProps } from '../types/dashboard'
import type { SimpleRepricingHistoryResponse } from '../types/dynamic-pricing'
import type { ProductFilter, Site, Tracker } from '../types/product'
import type { Notifications } from '../types/profile/notifications'
import type { Tag } from '../types/tag'
import type { Billing, CapAdjustment, Preferences } from '../types/user'

export const baseApi = createApi({
  baseQuery: customFetchBase,
  tagTypes: [
    'Categories',
    'Brands',
    'Products',
    'ProductCount',
    'Competitors',
    'Me',
    'AiSetup',
    'Billing',
    'Notifications',
    'Preferences',
    'Filters',
    'CompetitorPreferences',
    'BreakDownInfo',
    'DynamicPricingRuleSet',
    'DynamicPricingRuleSetCount',
    'DynamicPricingRuleSetTemplates',
    'Sidebar',
    'BatchImport',
    'BatchImportLatest',
    'PriceApiMatchStatus',
    'Export',
    'ExportCount',
    'ExportLatest',
    'SmartPriceByProduct',
    'FeedTargetMe',
    'FeedFields',
  ],
  endpoints: (builder) => ({
    dashboard: builder.query<DashboardTopbarResponse, void>({
      query: () => ({
        url: '/dashboard/sidebar',
        method: 'GET',
      }),
      providesTags: ['Sidebar'],
      transformResponse: (response: any) => ({
        ...response,
        topbar: {
          ...response.topbar,
          last_updated: response.topbar
            ? DateTime.fromISO(response.topbar.last_updated)
            : undefined,
        },
      }),
    }),
    sidebarIndex: builder.query<IndexProps, void>({
      query: () => ({
        url: '/dashboard/sidebar-index',
        method: 'GET',
      }),
    }),
    breakdownInfo: builder.query<BreakdownInfo[], void>({
      query: (arg) => ({
        url: '/dashboard/breakdown-info',
        method: 'GET',
      }),
      providesTags: ['BreakDownInfo'],
    }),

    collections: builder.query<
      PaginatedResponse<{ id: string; name: string }>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/products/collections-select',
        method: 'GET',
        params: arg,
      }),
    }),
    categoriesManage: builder.query<
      PaginatedResponse<Category>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/categories/manage',
        method: 'GET',
        params: arg,
      }),
      providesTags: (result) =>
        result && result.meta.totalItems > 0
          ? [
              ...result.data.map(({ id }) => ({
                type: 'Categories' as const,
                id,
              })),
              { type: 'Categories', id: 'LIST' },
            ]
          : [],
    }),
    competitorsManage: builder.query<
      PaginatedResponse<Competitor>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/competitors/manage',
        method: 'GET',
        params: arg,
      }),
      providesTags: ['Competitors'],
    }),

    competitorsByIdList: builder.query<
      { id: number; name: string }[],
      { idList: number[] }
    >({
      query: ({ idList }) => ({
        url: `/competitors/names`,
        method: 'GET',
        params: {
          ids: idList.join(','),
        },
      }),
    }),
    myAllCompetitorsIdList: builder.query<{ ids: number[] }, void>({
      query: () => ({
        url: `/competitors`,
        method: 'GET',
        cache: 'no-cache',
      }),
    }),
    brandsManage: builder.query<PaginatedResponse<Brand>, PaginatedQueryParams>(
      {
        query: (arg: PaginatedQueryParams) => ({
          url: '/brands/manage',
          method: 'GET',
          params: arg,
        }),
        providesTags: (result) =>
          result && result.meta.totalItems > 0
            ? [
                ...result.data.map(({ id }) => ({
                  type: 'Brands' as const,
                  id,
                })),
                { type: 'Brands', id: 'LIST' },
              ]
            : [],
      },
    ),
    companiesManage: builder.query<
      PaginatedResponse<Company>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/companies/manage',
        method: 'GET',
        params: arg,
      }),
    }),
    updateCategoryName: builder.mutation({
      query: ({ id, name }) => ({
        url: `/categories/${id}/rename`,
        method: 'PATCH',
        body: { name: name },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Categories', id: arg.id },
      ],
    }),
    updateBrandName: builder.mutation({
      query: ({ id, name }) => ({
        url: `/brands/${id}/rename`,
        method: 'PATCH',
        body: { name: name },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Brands', id: arg.id }],
    }),
    deleteBrand: builder.mutation<
      any,
      {
        id: number
        brand_id?: number
        brand_name?: string
      }
    >({
      query: (body) => ({
        url: '/brands/delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Brands'],
    }),
    deleteCategory: builder.mutation<
      any,
      {
        id: number
        category_id?: number
        category_name?: string
      }
    >({
      query: (body) => ({
        url: '/categories/delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Categories'],
    }),
    deleteCompetitors: builder.mutation<
      any,
      {
        body: {
          selected_rows: Array<number>
          unselected_rows: Array<number>
          select_all: boolean
        }
        params?: ProductFilter
      }
    >({
      query: ({ body, params }) => ({
        url: '/competitors/delete',
        method: 'POST',
        params: params,
        body,
      }),
      invalidatesTags: ['Competitors', 'CompetitorPreferences'],
    }),
    reports404: builder.query<PaginatedResponse<Tracker>, PaginatedQueryParams>(
      {
        query: (arg: PaginatedQueryParams) => ({
          url: '/site/tracker-reports',
          method: 'GET',
          params: arg,
        }),
        transformResponse: (response: PaginatedResponse<Tracker>) => {
          response.data = response.data.map((i) => ({
            ...i,
            date_created_display: DateTime.fromISO(i.date_created).toFormat(
              'dd LLL yyyy HH:mm',
            ),
            date_updated_display: i.date_updated
              ? DateTime.fromISO(i.date_updated).toFormat('dd LLL yyyy HH:mm')
              : null,
          }))
          return response
        },
      },
    ),
    reportsOpen: builder.query<
      PaginatedResponse<Tracker>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/site/url-reports?status=0',
        method: 'GET',
        params: arg,
      }),
      transformResponse: (response: PaginatedResponse<Tracker>) => {
        response.data = response.data.map((i) => ({
          ...i,
          date_created_display: DateTime.fromISO(i.date_created).toFormat(
            'dd LLL yyyy HH:mm',
          ),
          date_updated_display: i.date_updated
            ? DateTime.fromISO(i.date_updated).toFormat('dd LLL yyyy HH:mm')
            : null,
        }))
        return response
      },
    }),
    reportsClosed: builder.query<
      PaginatedResponse<Tracker>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/site/url-reports?status=1',
        method: 'GET',
        params: arg,
      }),
      transformResponse: (response: PaginatedResponse<Tracker>) => {
        response.data = response.data.map((i) => ({
          ...i,
          date_created_display: DateTime.fromISO(i.date_created).toFormat(
            'dd LLL yyyy HH:mm',
          ),
          date_updated_display: i.date_updated
            ? DateTime.fromISO(i.date_updated).toFormat('dd LLL yyyy HH:mm')
            : null,
        }))
        return response
      },
    }),
    brands: builder.query<PaginatedResponse<Brand>, PaginatedQueryParams>({
      query: (arg: PaginatedQueryParams) => ({
        url: '/brands',
        method: 'GET',
        params: arg,
      }),
    }),
    categories: builder.query<
      PaginatedResponse<Category>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/categories',
        method: 'GET',
        params: arg,
      }),
    }),
    competitors: builder.query<
      PaginatedResponse<Competitor>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/competitors/select',
        method: 'GET',
        params: arg,
      }),
      providesTags: ['Competitors'],
    }),
    tags: builder.query<PaginatedResponse<Tag>, PaginatedQueryParams>({
      query: (arg: PaginatedQueryParams) => ({
        url: '/companies/tags',
        method: 'GET',
        params: arg,
      }),
    }),
    companyProductLimits: builder.query<
      ProductLimits,
      void | { onResponse?: (response: ProductLimits) => void }
    >({
      query: () => ({
        url: '/companies/product-limits',
        method: 'GET',
      }),
      transformResponse: (response: ProductLimits, meta, arg) => {
        if (arg?.onResponse) {
          arg.onResponse(response)
        }
        return response
      },
    }),
    companiesForSelect: builder.query<
      PaginatedResponse<CompanyForSelect>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/companies',
        method: 'GET',
        params: arg,
      }),
    }),
    me: builder.query<any, void>({
      query: () => ({
        url: '/users/me',
        method: 'GET',
      }),
      providesTags: ['Me'],
      transformResponse: (response: any) => {
        if (response.is_onboarding_completed === 1) {
          for (const key of ['ob', 'onb', 'cs']) {
            localStorage.removeItem(
              `${ONBOARDING_STORAGE_KEY_PREFIX}:${key}:${response.id}`,
            )
          }
        }

        response.isShopify =
          response?.register_source === 'Shopify' &&
          response?.shopify_token != undefined
        return response
      },
    }),
    updateProfile: builder.mutation<void, {}>({
      query: (body: {
        username?: string
        email?: string
        password?: string
        current_password?: string
      }) => ({
        url: '/users/me/profile',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Me'],
    }),
    updateShopifyPreferences: builder.mutation<
      void,
      Partial<{
        shopify_sync_fields: string
        shopify_keep_sync: number
        shopify_settings_deletion_archived: boolean
        shopify_settings_deletion_draft: boolean
        shopify_set_prices: number
        shopify_settings_import_draft_and_archive: boolean
        shopify_settings_import_out_of_stock: boolean
        shopify_smart_price_calculation_mode: string
        shopify_show_price_as_it_is: boolean
        shopify_cap_adjustment: CapAdjustment
        // Set true to confirm switching repricing away from another no-catalog
        // panel after the backend returns NO_CATALOG_REPRICING_CONFLICT (409).
        force?: boolean
      }>
    >({
      query: (body) => ({
        url: '/users/me/shopify/preferences',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Preferences'],
    }),
    getRepricingConflict: builder.query<
      {
        active_panel: {
          company_id: number
          panel_name: string
          is_root: boolean
        } | null
        conflict_type?: 'NO_CATALOG' | 'SHARED_CATALOG' | null
      },
      void
    >({
      query: () => ({
        url: '/users/me/shopify/repricing-conflict',
        method: 'GET',
      }),
    }),
    meCompetitorPreferences: builder.query<any, void>({
      query: () => ({
        url: '/harvester/get-preference',
        method: 'GET',
      }),
      providesTags: ['CompetitorPreferences'],
    }),
    updateCompetitorPreferences: builder.mutation<void, {}>({
      query: (body: {
        competitors: {
          is_exclude: boolean
          include_list: string[]
          exclude_list: string[]
        }
        can_pause_competitor?: boolean
        companyHarvesterProviders: {
          harvesterProvider: {
            provider: HarvesterProviderList
            channel: HarvesterChannelList
            country: string
          }
          options: {
            is_buybox: boolean
            is_own_offering: boolean
          }
        }[]
      }) => ({
        url: '/harvester/update-preference',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['CompetitorPreferences'],
    }),
    mePreferences: builder.query<Preferences, void>({
      query: () => ({
        url: '/users/me/preferences',
        method: 'GET',
      }),
      providesTags: ['Preferences'],
    }),

    // `switched_total_price_rules`: turning Total Price off switches the rules
    // that used it to item price in the same save (WEG-2327). A backend from
    // before that change returns an empty body, which arrives as null.
    updateMePreferences: builder.mutation<
      { switched_total_price_rules?: number } | null,
      {}
    >({
      query: (body: {
        total_price?: number
        is_deduct_additional_cost?: boolean
        total_price_calculation?: string
        scrape_from?: string
        vat_type?: string
        show_stock_out?: boolean
        include_stock_out?: boolean
        currency_id?: number
        price_change_threshold?: string
        price_change_threshold_type?: string
        decimal_delimiter_char?: string

        scrape_product_json_feed_url?: string
        scrape_product_base_url?: string
        scrape_product_feed_status?: string
        scrape_product_validation_date?: string
      }) => ({
        url: '/users/me/preferences',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Preferences', 'Me'],
    }),
    meBilling: builder.query<Billing, void>({
      query: () => ({
        url: '/users/me/billing',
        method: 'GET',
      }),
      providesTags: ['Billing'],
    }),
    meBillingPlans: builder.query<any, void>({
      query: () => ({
        url: '/users/me/billing/plans',
        method: 'GET',
      }),
      providesTags: ['Billing'],
    }),
    createSubscription: builder.mutation<void, UpgradeSubscriptionQueryArgs>({
      query: (body) => ({
        url: '/payment/create-subscription/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Billing', 'Me'],
    }),
    upgradeSubscription: builder.mutation<void, UpgradeSubscriptionQueryArgs>({
      query: (body) => ({
        url: '/payment/upgrade-subscription/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Billing', 'Me'],
    }),
    updateCreditCard: builder.mutation<void, UpdateCreditCardQueryArgs>({
      query: (body) => ({
        url: '/payment/credit-card/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Billing', 'Me'],
    }),
    oneTimePayment: builder.mutation<void, UpgradeSubscriptionQueryArgs>({
      query: (body) => ({
        url: '/payment/one-time-payment',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Billing', 'Me'],
    }),
    listInvoices: builder.query<
      PaginatedResponse<SimpleInvoiceResponse>,
      PaginatedQueryParams
    >({
      query: (params: PaginatedQueryParams) => ({
        url: '/billing/invoices',
        method: 'GET',
        params,
      }),
      providesTags: ['Billing'],
      transformResponse: (
        response: PaginatedResponse<SimpleInvoiceResponse>,
      ) => {
        response.data = response.data.map((i) => ({
          ...i,
          date_display: DateTime.fromISO(i.date).toFormat('dd LLL yyyy HH:mm'),
        }))
        return response
      },
    }),
    downloadInvoice: builder.mutation<InvoiceDownloadResponse, any>({
      query: ({ id }) => ({
        url: `/billing/invoices/${id}/download`,
        method: 'GET',
      }),
    }),

    currencies: builder.query({
      query: () => ({
        url: '/currencies',
        method: 'GET',
      }),
    }),
    notifications: builder.query<
      PaginatedResponse<Notifications>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/notifications',
        method: 'GET',
        params: arg,
      }),
      transformResponse: (response: PaginatedResponse<Notifications>) => {
        response.data = response.data.map((item) => {
          const transformedItem = { ...item }
          transformedItem.content.subject =
            transformedItem.content.subject.charAt(0).toUpperCase() +
            transformedItem.content.subject.slice(1)
          if (transformedItem.content.options === null) {
            transformedItem.content.options = 'All'
          } else {
            transformedItem.content.options =
              transformedItem.content.option_names
          }
          return transformedItem
        })
        return response
      },
      providesTags: ['Notifications'],
    }),
    createNotification: builder.mutation<
      any,
      {
        emailList: Array<{
          id?: number
          email_address?: string
        }>
        options: string[]
        contentType: string
        frequency: string
        subject: string
      }
    >({
      query: (body) => ({
        url: '/notifications',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Notifications'],
    }),
    deleteNotification: builder.mutation<
      any,
      {
        id: string
      }
    >({
      query: (param) => ({
        url: `/notifications/${param.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notifications'],
    }),
    validateFeedJson: builder.mutation<
      {
        baseUrl: string
        isValid: boolean
        message: string
        validationDate: string
      },
      any
    >({
      query: (body) => ({
        url: '/json-feed/validation',
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => ({
        ...response,
        message: response.message == 'Success' ? 'Validated' : response.message,
      }),
    }),
    getWhitelistIPInfo: builder.query<any, any>({
      query: () => ({
        url: '/json-feed/whitelist',
        method: 'GET',
      }),
    }),
    getHistoryIndexSites: builder.query<any, any>({
      query: () => ({
        url: 'dashboard/historical-index/sites',
        method: 'GET',
      }),
    }),
    getHistoryIndexChart: builder.query<
      any,
      {
        siteIds: Array<any>
        startAt: string
        endAt: string
      }
    >({
      query: (params) => ({
        url: 'dashboard/historical-index/chart',
        method: 'GET',
        params,
      }),
      // Switching tabs/date-range flips through a handful of filter
      // combinations (7 days / 30 days / 2 months, same site selection) —
      // keep each one's result cached well past the default 60s so
      // flipping back to a previously-viewed tab doesn't refetch.
      keepUnusedDataFor: 300,
    }),
    getBrandNamesFromIds: builder.query<
      any,
      {
        ids: any
      }
    >({
      query: ({ ids }) => ({
        url: '/brands/names',
        method: 'GET',
        params: { ids: ids },
      }),
    }),
    getCompanyNameFromId: builder.query<
      any,
      {
        ids: any
      }
    >({
      query: ({ ids }) => ({
        url: 'companies/names',
        method: 'GET',
        params: { ids: ids },
      }),
    }),
    getCategoryNamesFromIds: builder.query<
      any,
      {
        ids: any
      }
    >({
      query: ({ ids }) => ({
        url: '/categories/names',
        method: 'GET',
        params: { ids: ids },
      }),
    }),
    getTagNamesFromIds: builder.query<
      any,
      {
        ids: any
      }
    >({
      query: ({ ids }) => ({
        url: '/products/tags/names',
        method: 'GET',
        params: { ids: ids },
      }),
    }),
    listRepricingHistory: builder.query<
      PaginatedResponse<SimpleRepricingHistoryResponse>,
      PaginatedQueryParams
    >({
      query: (params: PaginatedQueryParams) => ({
        url: '/dynamic-pricing/history',
        method: 'GET',
        params,
      }),
      // .toRelative()
      transformResponse: (response: any) => ({
        ...response,
        data: response.data.map((item: any) => ({
          ...item,
          repricingTime: DateTime.fromISO(item.repricingTime).toLocaleString({
            dateStyle: 'medium',
            timeStyle: 'medium',
          }),
        })),
      }),
    }),
    manageDeleteCompetitors: builder.mutation<
      any,
      {
        body: {
          selected_rows: Array<number>
          unselected_rows: Array<number>
          select_all: boolean
          filter?: ProductFilter
          expected_count?: number
        }
      }
    >({
      query: ({ body }) => ({
        url: 'manage/competitor/delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Competitors', 'CompetitorPreferences'],
    }),
    listDomain: builder.query<PaginatedResponse<Site>, PaginatedQueryParams>({
      query: (arg: PaginatedQueryParams) => ({
        url: '/site/domain-list',
        method: 'GET',
        params: arg,
      }),
    }),
  }),
})

export const {
  useDashboardQuery,
  useBreakdownInfoQuery,
  useSidebarIndexQuery,
  useLazyCollectionsQuery,
  useLazyCategoriesManageQuery,
  useLazyCompetitorsManageQuery,
  useLazyBrandsManageQuery,
  useLazyReports404Query,
  useLazyReportsOpenQuery,
  useLazyReportsClosedQuery,
  useBrandsQuery,
  useLazyBrandsQuery,
  useLazyCategoriesQuery,
  useLazyCompetitorsQuery,
  useLazyTagsQuery,
  useMeQuery,
  useMeBillingQuery,
  useMeBillingPlansQuery,
  useMePreferencesQuery,
  useMeCompetitorPreferencesQuery,
  useUpdateCompetitorPreferencesMutation,
  useUpdateProfileMutation,
  useUpdateMePreferencesMutation,
  useUpdateShopifyPreferencesMutation,
  useLazyGetRepricingConflictQuery,
  useLazyMeQuery,
  useCurrenciesQuery,
  useLazyNotificationsQuery,
  useUpdateBrandNameMutation,
  useUpdateCategoryNameMutation,
  useDeleteBrandMutation,
  useDeleteCategoryMutation,
  useDeleteCompetitorsMutation,
  useLazyCompanyProductLimitsQuery,
  useLazyCompaniesForSelectQuery,
  useCreateNotificationMutation,
  useDeleteNotificationMutation,
  useValidateFeedJsonMutation,
  useGetWhitelistIPInfoQuery,
  useGetHistoryIndexSitesQuery,
  useLazyGetHistoryIndexChartQuery,
  useLazyGetCompanyNameFromIdQuery,
  useLazyGetBrandNamesFromIdsQuery,
  useGetBrandNamesFromIdsQuery,
  useLazyGetCategoryNamesFromIdsQuery,
  useGetCategoryNamesFromIdsQuery,
  useLazyGetTagNamesFromIdsQuery,
  useCompetitorsByIdListQuery,
  useLazyMyAllCompetitorsIdListQuery,
  useListInvoicesQuery,
  useDownloadInvoiceMutation,
  useUpgradeSubscriptionMutation,
  useCreateSubscriptionMutation,
  useUpdateCreditCardMutation,
  useOneTimePaymentMutation,
  useListRepricingHistoryQuery,
  useManageDeleteCompetitorsMutation,
  useLazyListDomainQuery,
} = baseApi
